import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';
import { sendManualSMS, updateAIAutoReply } from '@/api/textingPlatform';
import { SendManualSMSRequest, UpdateAIAutoReplyRequest } from '@/interfaces/ITextingPlatform';

interface UseMessageActionsProps {
	selectedClient: string;
	selectedAgentId: string;
	provisionedNumber: string;
	theirNumber: string;
	username: string;
	localMessages: any;
	setLocalMessages: (updater: (prev: any) => any) => void;
	onMessageSent?: () => void;
}

export const useMessageActions = ({
	selectedClient,
	selectedAgentId,
	provisionedNumber,
	theirNumber,
	username,
	setLocalMessages,
	onMessageSent,
}: UseMessageActionsProps) => {
	const [msgVal, setMsgVal] = useState<string>('');
	const queryClient = useQueryClient();

	const sendMessageMutation = useMutation({
		mutationFn: (request: SendManualSMSRequest) => sendManualSMS(request),
		onSuccess: () => {
			if (selectedAgentId && provisionedNumber) {
				queryClient.invalidateQueries({
					queryKey: ['getTextingConversations', selectedAgentId, provisionedNumber],
				});
			}
		},
		onError: (error: Error) => {
			notification.error({
				message: 'Error',
				description: error?.message || 'Failed to send message',
			});
		},
	});

	const updateAIAutoReplyMutation = useMutation({
		mutationFn: (request: UpdateAIAutoReplyRequest) => updateAIAutoReply(request),
		onSuccess: (_, variables) => {
			if (username) {
				queryClient.invalidateQueries({
					queryKey: ['getDisabledAutoReplySettings', username],
				});
			}

			const enabled = !variables.disableAiAutoReply;
			notification.success({
				message: 'Success',
				description: `AI AutoReply ${enabled ? 'enabled' : 'disabled'}`,
			});
		},
		onError: (error: Error) => {
			notification.error({
				message: 'Error',
				description: error?.message || 'Failed to update AI AutoReply settings',
			});
		},
	});

	const handleSendMessage = useCallback(async () => {
		if (!msgVal?.trim()) {
			notification.error({
				message: 'Error',
				description: 'Please enter a message',
			});
			return;
		}

		if (!selectedClient || !provisionedNumber || !theirNumber) {
			notification.error({
				message: 'Error',
				description: 'Missing required information to send message',
			});
			return;
		}

		const messageText = msgVal.trim();
		const tempId = `temp_${Date.now()}`;

		const newMessage = {
			message: messageText,
			from: provisionedNumber,
			to: selectedClient,
			timestamp: new Date().toISOString(),
			tempId,
		};

		setLocalMessages((prev) => {
			if (!prev) {
				return prev;
			}

			return {
				...prev,
				[selectedClient]: {
					...prev[selectedClient],
					client_number: selectedClient,
					messages: [...(prev[selectedClient]?.messages || []), newMessage],
				},
			};
		});

		setMsgVal('');

		onMessageSent?.();

		sendMessageMutation.mutate(
			{
				message: messageText,
				from: provisionedNumber,
				theirNumber,
				to: selectedClient,
			},
			{
				onError: () => {
					setLocalMessages((prev) => {
						if (!prev) {
							return prev;
						}

						return {
							...prev,
							[selectedClient]: {
								...prev[selectedClient],
								messages:
									prev[selectedClient]?.messages?.filter((msg: any) => msg.tempId !== tempId) || [],
							},
						};
					});
				},
			},
		);
	}, [
		msgVal,
		selectedClient,
		provisionedNumber,
		theirNumber,
		setLocalMessages,
		onMessageSent,
		sendMessageMutation,
	]);

	const handleToggleAIAutoReply = useCallback(
		(newState: boolean) => {
			if (!selectedClient || !username) {
				notification.error({
					message: 'Error',
					description: 'Missing information to update AI settings',
				});
				return;
			}

			updateAIAutoReplyMutation.mutate({
				toNumber: selectedClient,
				disableAiAutoReply: !newState,
				username,
			});
		},
		[selectedClient, username, updateAIAutoReplyMutation],
	);

	const handleKeyPress = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				handleSendMessage();
			}
		},
		[handleSendMessage],
	);

	return {
		msgVal,
		setMsgVal,
		handleSendMessage,
		handleToggleAIAutoReply,
		handleKeyPress,
		sendMessageMutation,
		updateAIAutoReplyMutation,
	};
};
